import { useQuery, UseQueryResult } from "react-query";
import { UserOrEmailMapping } from "../../../features/Admin/typings/admin.types";
import { UserRole } from "../../../features/Login/typings/auth.types";
import { getLocAdmins } from "../../../utils/axios-requests";
import { getUserIdsOfUserOrEmailMapping } from "../../../utils/type/type.utils";

export type UseIsUserServiceAdminForLocationResult = UseQueryResult<boolean, unknown> & {
  /**
   * True only while the location request-admin list is loading for an **enabled** query.
   * When the query is disabled (no request-admin role or invalid ids), this is always false —
   * do not treat `data === undefined` as loading in that case.
   */
  isServiceAdminLookupPending: boolean;
};

/**
 * True when the user has the request-admin realm role and is assigned as Service Admin
 * for the given location (same source as Admin → Service Admin per location).
 */
export function useIsUserServiceAdminForLocation(
  locationInventoryId: number | undefined,
  userId: string | undefined,
  isRequestAdminRealmRole: boolean
): UseIsUserServiceAdminForLocationResult {
  const enabled =
    isRequestAdminRealmRole &&
    locationInventoryId != null &&
    locationInventoryId > 0 &&
    userId != null &&
    userId.length > 0;

  const query = useQuery(
    ["loc-request-admins", locationInventoryId, userId],
    async (): Promise<boolean> => {
      const res = await getLocAdmins(
        locationInventoryId as number,
        UserRole.isRequestAdmin,
        undefined
      );
      const admins = (res.data ?? []) as UserOrEmailMapping[];
      const ids = getUserIdsOfUserOrEmailMapping(admins);
      return ids.includes(userId as string);
    },
    { enabled, staleTime: 5 * 60 * 1000, retry: 1 }
  );

  return {
    ...query,
    isServiceAdminLookupPending: Boolean(enabled && query.isLoading)
  };
}
